import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { FormCard } from 'common/components/FormCard';
import { ListFilters, useAppContext } from 'common/context/AppContext';
import { useListFilters } from 'common/util/useListFilters';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';





interface ListCardProps<T> {
    loading: boolean
    children?: React.ReactNode
    title: string
    createName?: string
    createRoute?: string
    loadData: () => void
    columns: ColumnsType<T>
    listData?: ListData<T>
    filters: ListFilters
    setFilters: (filters: ListFilters) => void
    initialFilters?: ListFilters
    additionalButtons?: AdditionalButton[]
    extra?: React.ReactNode
    listRouteKey?: string
    tableLayout?: 'auto' | 'fixed'
    scrollX?: number | string
}

interface ListData<T> {
    data?: Array<ItemData<T>>
    pagination?: ListPagination
}

interface ListPagination {
    size?: number;
    currentPage?: number;
    totalPages?: number;
    total?: number;
}

interface ItemData<T> {
    data?: T;
}

interface AdditionalButton {
    name: string,
    onClick: () => void
}

export const ListCard = <T extends object>(
    {
        children,
        title,
        loading,
        createName,
        createRoute,
        loadData,
        columns,
        listData,
        filters,
        setFilters,
        initialFilters,
        additionalButtons,
        extra,
        listRouteKey,
        tableLayout,
        scrollX
    }: ListCardProps<T>) => {

    const {setFilter, setFilter2, clearFilters} = useListFilters(setFilters, initialFilters)
    const navigateTo = useNavigate()
    const appContext = useAppContext()
    const pendingNavigation = listRouteKey ? appContext.pendingListNavigation?.get(listRouteKey) : undefined
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })
    const [internalColumns, setInternalColumns] = useState<Array<any>>([])

    function setDefaultSortOrder(column) {
        if (filters.sort && filters.sort.name && column['dataIndex'] === filters.sort.name) {
            const sortOrder = filters.sort.direction === 'asc' ? 'ascend' : 'descend'
            return {...column, sortOrder}
        } else {
            const newColumn = {...column}
            delete newColumn['sortOrder']
            return newColumn
        }

    }

    useEffect(() => {
        loadData()
    }, [filters])

    useEffect(() => {
        if (listData !== undefined) {
            const currentPage = listData.pagination?.currentPage
            const totalPages = listData.pagination?.totalPages
            const sortName = filters.sort?.name
            const sortDirection = filters.sort?.direction

            if (pendingNavigation?.repositionByModifiedDate) {
                const pendingListNavigation = new Map(appContext.pendingListNavigation)
                if (listRouteKey) {
                    pendingListNavigation.delete(listRouteKey)
                }
                appContext.setPendingListNavigation(pendingListNavigation)

                if (sortName === 'modified') {
                    const targetPage = sortDirection === 'asc' ? totalPages : 1
                    if (targetPage && targetPage !== currentPage) {
                        setFilter('page', targetPage)
                        return
                    }
                }
            }

            if ((listData.data === undefined || listData.data.length === 0) && filters.page && filters.page > 1) {
                // maybe we delete or deactivate the last item on the last page
                setFilter('page', filters.page - 1)
            } else {
                const pageResponse = listData?.pagination
                setPagination((prev: any) => {
                    if (prev.total === pageResponse?.total && prev.current === pageResponse?.currentPage) {
                        return prev
                    }
                    return {
                        ...prev,
                        total: pageResponse?.total,
                        current: pageResponse?.currentPage,
                    }
                })
            }
        }
    }, [appContext.pendingListNavigation, appContext.setPendingListNavigation, filters.page, filters.sort?.direction, filters.sort?.name, listData, listRouteKey, pendingNavigation, setFilter])

    useEffect(() => {
        const columnsWithSortIcon = (columns).filter(Boolean).map(setDefaultSortOrder)
        setInternalColumns(columnsWithSortIcon)
    }, [columns]);

    const onSort = (sortingResult: SorterResult<T>) => {
        setFilter(
            'sort',
            sortingResult.order && sortingResult.column?.dataIndex
                ? {
                    name: sortingResult.column.dataIndex as string,
                    direction: sortingResult.order === 'ascend' ? 'asc' : 'desc'
                }
                : undefined
        );
    }

    return <FormCard title={title} extra={
        <Flex gap={8} align="center">
            {extra}
            {createRoute &&<Button htmlType="button" type="primary" onClick={() => {
                clearFilters()
                navigateTo(createRoute)
            }}><PlusOutlined/>New {createName}</Button>}
            {additionalButtons?.map((button) => (
                <Button htmlType="button" type="primary" onClick={button.onClick}>
                    {button.name}
                </Button>
            ))}
        </Flex>
    }>
        <Flex vertical gap={20}>
            {children && (
                <Flex gap={8} wrap="wrap">
                    {children}
                </Flex>
            )}
            <Table
                rowKey="id"
                size="middle"
                showSorterTooltip={false}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    onChange: (page, pageSize) => {
                        if (pageSize !== (filters.pageSize ?? pagination.pageSize)) {
                            setFilter2('pageSize', pageSize, 'page', 1)
                        } else {
                            setFilter('page', page)
                        }
                    },
                    position: ['bottomRight']
                }}
                dataSource={listData?.data?.map(item => item.data || {} as T)}
                sortDirections={["ascend", "descend", "ascend"]}
                onChange={(_, _1, sortingResult) => onSort(sortingResult as SorterResult<T>)}
                className="list-table"
                loading={loading}
                columns={internalColumns}
                tableLayout={tableLayout}
                scroll={{ x: scrollX ?? 'max-content'}}
            />
        </Flex>
    </FormCard>
}
